/**
 * SettingsPage — `/settings`
 *
 * Sections: Language, Theme, Account, Notifications, Sign out.
 * RTL: wraps in dir={lang === "ar" ? "rtl" : "ltr"}.
 */

import * as React from 'react';
import { useLocation } from 'wouter';
import { FadeIn, StaggerList, StaggerItem } from '@workspace/ui';

import { useAuth } from '../providers/AuthProvider';
import { useLang } from '../providers/LangProvider';

// ── Theme helpers ─────────────────────────────────────────────────────────────

type Theme = 'light' | 'dark' | 'system';
const THEME_KEY = 'superall_theme';

function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'system';
  const raw = localStorage.getItem(THEME_KEY);
  if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  return 'system';
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    // system
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }
}

// ── Notification permission helpers ──────────────────────────────────────────

function getPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function SettingsPage(): React.ReactElement {
  const [lang, setLang] = useLang();
  const { user, logout } = useAuth();
  const [, navigate] = useLocation();

  const isAr = lang === 'ar';

  // Theme
  const [theme, setThemeState] = React.useState<Theme>(getStoredTheme);

  // Apply saved theme on mount
  React.useEffect(() => {
    applyTheme(theme);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleTheme(next: Theme) {
    setThemeState(next);
    localStorage.setItem(THEME_KEY, next);
    applyTheme(next);
  }

  // Notification permission
  const [permission, setPermission] = React.useState<NotificationPermission | 'unsupported'>(
    getPermission
  );

  async function requestNotifPermission() {
    if (!('Notification' in window)) return;
    const result = await Notification.requestPermission();
    setPermission(result);
  }

  // Sign out
  function handleSignOut() {
    logout();
    navigate('/login');
  }

  // Labels
  const title = isAr ? 'الإعدادات' : 'Settings';
  const langLabel = isAr ? 'اللغة' : 'Language';
  const themeLabel = isAr ? 'المظهر' : 'Theme';
  const accountLabel = isAr ? 'الحساب' : 'Account';
  const notifsLabel = isAr ? 'الإشعارات' : 'Notifications';
  const signOutLabel = isAr ? 'تسجيل الخروج' : 'Sign out';

  const permissionText: Record<NotificationPermission | 'unsupported', string> = {
    granted: isAr ? 'مُفعَّل' : 'Granted',
    denied: isAr ? 'محظور في إعدادات المتصفح' : 'Blocked in browser settings',
    default: isAr ? 'غير محدد' : 'Not set',
    unsupported: isAr ? 'غير مدعوم' : 'Not supported',
  };

  const themeOptions: Array<{ value: Theme; label: string; emoji: string }> = [
    { value: 'light', label: isAr ? 'فاتح' : 'Light', emoji: '☀️' },
    { value: 'dark', label: isAr ? 'داكن' : 'Dark', emoji: '🌙' },
    { value: 'system', label: isAr ? 'النظام' : 'System', emoji: '💻' },
  ];

  const sections = [
    { key: 'language' },
    { key: 'theme' },
    { key: 'account' },
    { key: 'notifications' },
  ];

  return (
    <FadeIn>
      <div dir={isAr ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            aria-label={isAr ? 'رجوع' : 'Back'}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
          >
            {isAr ? '→' : '←'}
          </button>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">{title}</h1>
        </div>

        <StaggerList className="space-y-4">
          {/* ── Language ───────────────────────────────────────────── */}
          <StaggerItem>
            <section className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-5 shadow-sm">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {langLabel}
              </h2>
              <div className="flex gap-3">
                {(['en', 'ar'] as const).map((l) => (
                  <label
                    key={l}
                    className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${
                      lang === l
                        ? 'border-purple-600 bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400'
                        : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                    }`}
                  >
                    <input
                      type="radio"
                      name="language"
                      value={l}
                      checked={lang === l}
                      onChange={() => setLang(l)}
                      className="sr-only"
                    />
                    {l === 'en' ? 'English' : 'العربية'}
                  </label>
                ))}
              </div>
            </section>
          </StaggerItem>

          {/* ── Theme ─────────────────────────────────────────────── */}
          <StaggerItem>
            <section className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-5 shadow-sm">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {themeLabel}
              </h2>
              <div className="flex gap-2">
                {themeOptions.map(({ value, label, emoji }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleTheme(value)}
                    className={`flex flex-1 flex-col items-center gap-1 rounded-xl border px-3 py-3 text-xs font-medium transition-colors ${
                      theme === value
                        ? 'border-purple-600 bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400'
                        : 'border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                    }`}
                    aria-pressed={theme === value}
                  >
                    <span className="text-xl" aria-hidden="true">
                      {emoji}
                    </span>
                    {label}
                  </button>
                ))}
              </div>
            </section>
          </StaggerItem>

          {/* ── Account ───────────────────────────────────────────── */}
          <StaggerItem>
            <section className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-5 shadow-sm">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {accountLabel}
              </h2>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {isAr ? 'الاسم' : 'Name'}
                  </span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {user?.fullName ?? (isAr ? 'غير محدد' : '—')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {isAr ? 'البريد الإلكتروني' : 'Email'}
                  </span>
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">
                    {user?.email ?? (isAr ? 'غير محدد' : '—')}
                  </span>
                </div>
              </div>
              <a
                href="/profile"
                className="mt-4 inline-block rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-4 py-2 text-sm font-medium text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/30 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
              >
                {isAr ? 'تعديل الملف الشخصي' : 'Edit profile'}
              </a>
            </section>
          </StaggerItem>

          {/* ── Notifications ─────────────────────────────────────── */}
          <StaggerItem>
            <section className="rounded-2xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-5 py-5 shadow-sm">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {notifsLabel}
              </h2>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {isAr ? 'إشعارات المتصفح' : 'Enable browser notifications'}
                  </p>
                  <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                    {permissionText[permission]}
                  </p>
                </div>
                {permission === 'default' && (
                  <button
                    type="button"
                    onClick={() => void requestNotifPermission()}
                    className="rounded-xl bg-purple-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-800 focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
                  >
                    {isAr ? 'تفعيل' : 'Enable'}
                  </button>
                )}
                {permission === 'granted' && (
                  <span className="rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                    {isAr ? 'مُفعَّل' : 'Active'}
                  </span>
                )}
                {permission === 'denied' && (
                  <span className="rounded-full bg-red-100 dark:bg-red-900/40 px-2 py-0.5 text-xs font-semibold text-red-700 dark:text-red-400">
                    {isAr ? 'محظور' : 'Blocked'}
                  </span>
                )}
              </div>
            </section>
          </StaggerItem>

          {/* ── Sign out ──────────────────────────────────────────── */}
          <StaggerItem>
            <button
              type="button"
              onClick={handleSignOut}
              className="w-full rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 px-5 py-4 text-sm font-semibold text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              {signOutLabel}
            </button>
          </StaggerItem>
        </StaggerList>
      </div>
    </FadeIn>
  );
}
